import { useState } from 'react';
import type { Kind } from '@sflx/shared';
import { state } from '../lib/api';
import { Icon } from './Icon';

interface FavoriteButtonProps {
  accountId: string;
  kind: Kind;
  xtreamId: number;
  favorite: boolean;
  onChange: (favorite: boolean) => void;
  size?: number;
}

/**
 * Optimistic favorite toggle: the star flips immediately and rolls back if the
 * write fails, because a 200ms wait on a list row reads as a dropped click.
 */
export function FavoriteButton({ accountId, kind, xtreamId, favorite, onChange, size = 18 }: FavoriteButtonProps) {
  const [busy, setBusy] = useState(false);

  const toggle = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (busy) return;
    const next = !favorite;
    setBusy(true);
    onChange(next);
    try {
      await state.favorite(accountId, kind, xtreamId, next);
    } catch {
      onChange(favorite);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className="fav"
      aria-pressed={favorite}
      aria-label={favorite ? 'Remove from favorites' : 'Add to favorites'}
      onClick={toggle}
    >
      <Icon name={favorite ? 'starFilled' : 'star'} size={size} />
    </button>
  );
}
